import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription, timer } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { FeatureKey, Rights } from 'src/app/_models';
import { BaseServices } from '../../kuba.services';
import { DeviationServices } from '../../follow-ups/services/deviation.services';
import { DeviationListDto } from '../../follow-ups/models/deviationListDto';

/** A business-scoped view of the shared register; findings remain the creation entry point. */
@Component({
  selector: 'flexi-safety-round-deviations',
  standalone: false,
  templateUrl: './safety-round-deviations.component.html',
  styleUrls: ['./safety-round-list.component.scss']
})
export class SafetyRoundDeviationsComponent implements OnInit, OnDestroy {
  rows: DeviationListDto[] = [];
  loading = false;
  failed = false;
  title = '';
  status = '';
  page = 1;
  total = 0;
  readonly pageSize = 20;
  private request?: Subscription;
  private pendingSearch?: Subscription;
  private loadedBusinessId: number | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private deviations: DeviationServices,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  get businessId(): number {
    const businessRoute = this.route.pathFromRoot.find(
      route => route.routeConfig?.path === 'business/:id'
    );
    return (
      Number(businessRoute?.snapshot.paramMap.get('id')) ||
      BaseServices.BusinessId
    );
  }

  ngOnInit(): void {
    this.load();
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (
        event instanceof NavigationEnd &&
        this.loadedBusinessId !== this.businessId
      ) {
        this.pendingSearch?.unsubscribe();
        this.title = '';
        this.status = '';
        this.page = 1;
        this.load();
      }
    });
  }
  ngOnDestroy(): void {
    this.pendingSearch?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  queueSearch(): void {
    this.pendingSearch?.unsubscribe();
    this.pendingSearch = timer(300)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.search());
  }

  search(): void {
    this.pendingSearch?.unsubscribe();
    this.page = 1;
    this.load();
  }
  changePage(page: number): void {
    this.page = page;
    this.load();
  }

  load(): void {
    this.request?.unsubscribe();
    const businessId = this.businessId;
    this.loadedBusinessId = businessId;
    this.rows = [];
    this.total = 0;
    this.loading = true;
    this.failed = false;
    const searchText = this.title.trim();
    const isDeviationNumber = /^(?:[a-z]{2,}-)?\d+$/i.test(searchText);
    const role = BaseServices.UserRole;
    const canViewAll =
      role === 'Admin' ||
      role === 'Editor' ||
      role === 'Portal' ||
      BaseServices.checkUserRights(Rights.ADMINISTRATE_DEVIATION, role) ||
      BaseServices.checkUserRights(Rights.VIEW_DEVIATION, role);
    this.request = this.deviations
      .getDeviationListPagedByBusinessId(
        BaseServices.ApplicationId,
        { pageNumber: this.page, pageSize: this.pageSize },
        businessId,
        {
          clientId: String(FeatureKey.FLEXI_SAFETY_ROUNDS),
          title: !isDeviationNumber && searchText ? searchText : undefined,
          no: isDeviationNumber ? searchText.toUpperCase() : undefined,
          status: this.status || undefined,
          myOwnDeviation: !canViewAll,
          userId: BaseServices.UserId
        }
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: result => {
          if (businessId !== this.businessId) return;
          this.rows = result.Items;
          this.total = result.TotalCount;
        },
        error: () => {
          this.rows = [];
          this.total = 0;
          this.failed = true;
        }
      });
  }

  get safetyRoundsUrl(): string {
    return `${this.router.url.split('/Flexi')[0]}/Flexi/safety-rounds`;
  }

  deviationUrl(id: number): string {
    return `${this.router.url.split('/Flexi')[0]}/follow-up/deviations/edit/${id}`;
  }
}
